<template>
  <reports>
    <report
      data-test-id="open_loans"
      date-filter-incl-today-from="00:00"
      :sources="[
        {
          url: '/api/1/broker-reports/open-loans/export',
          defaultName: 'report_open_contracts.csv',
        },
      ]"
      title="Open Contracts per Company"
      with-company-filter
      with-date-filter
    >
      A report showing details of open contracts per day.
    </report>
    <report
      data-test-id="ctrpty_contract_balance"
      date-filter-incl-today-from="00:00"
      :sources="[
        {
          url: '/api/1/broker-reports/counterparty-contract-balance/export',
          defaultName: 'report_counterparty_contract_balance.csv',
        },
        {
          url: '/api/1/broker-reports/counterparty-contract-balance-pdf/export',
          defaultName: 'report_counterparty_contract_balance.pdf',
        },
      ]"
      title="SFT Counterparty Contract Balance per Company"
      with-company-filter
      with-date-filter
    >
      A report showing counterparty balances per side for current open positions.
    </report>
    <report
      data-test-id="bps_intact"
      :sources="[{ url: '/api/1/broker-reports/bps-intact/export', defaultName: 'bps-intact.csv' }]"
      title="BPS INTACT"
      with-company-filter
      with-date-filter
      with-pretty-option
    >
      A report with loan activity in BPS INTACT format.
    </report>
    <report
      data-test-id="bps_ib"
      date-filter-incl-today-from="10:00"
      :sources="[
        {
          url: '/api/1/broker-reports/security-grouped-balance/export',
          defaultName: 'report_security_grouped_balance.csv',
        },
        {
          url: '/api/1/broker-reports/security-grouped-balance-pdf/export',
          defaultName: 'report_security_grouped_balance.pdf',
        },
      ]"
      title="Daily Income Analysis"
      with-company-filter
      with-date-filter
    >
      A report showing security grouped balances.
    </report>
    <report
      data-test-id="income_analysis"
      date-filter-incl-today-from="10:00"
      description=""
      :sources="[
        {
          url: '/api/1/broker-reports/security-grouped-balance-month-to-date/export',
          defaultName: 'report_security_grouped_balance_month_to_date.csv',
        },
        {
          url: '/api/1/broker-reports/security-grouped-balance-month-to-date-pdf/export',
          defaultName: 'report_security_grouped_balance_month_to_date.pdf',
        },
      ]"
      title="Monthly Income Analysis"
      with-company-filter
      with-date-filter
    >
      A report showing month to date security grouped balances.
    </report>
    <report
      data-test-id="estimated_pnl"
      date-filter-incl-today-from="10:00"
      :sources="[
        {
          url: '/api/1/broker-reports/open-loans/export?pnl=true',
          defaultName: 'report_estimated_pnl.csv',
        },
      ]"
      title="Estimated PnL Report"
      url="/api/1/reports/company-open-loans/export?pnl=true"
      with-company-filter
      with-date-filter
    >
      A report approximating total PnL on open positions considering reinvestment opportunities and
      rebate income (loss).
    </report>
    <report
      data-test-id="benchmark_rate_history"
      date-filter-incl-today-from="00:00"
      :sources="[
        {
          url: '/api/1/broker-reports/benchmark-rates/export',
          defaultName: 'report_benchmark_rate_history.csv',
        },
      ]"
      title="Benchmark Rate History"
      with-company-filter
    >
      A report showing the benchmark rate used for the company’s PnL calculations overtime;
      defaulted to OBFR unless overridden by a company user.
    </report>
    <report
      data-test-id="position_movement_activity"
      date-filter-incl-today-from="00:00"
      :sources="[
        {
          url: '/api/1/broker-reports/activity/export',
          defaultName: 'report_position_movement_activity.csv',
        },
      ]"
      title="Position Movement Activity"
      with-company-filter
      with-date-filter
    >
      A report showing daily activity that impacts the shares outstanding; excludes rerates,
      mark-to-market, etc.
    </report>
    <report
      data-test-id="mark_to_market"
      date-filter-incl-today-from="21:00"
      :sources="[
        {
          url: '/api/1/broker-reports/mark-to-market/export',
          defaultName: 'report_mark_to_market.csv',
        },
      ]"
      title="Mark-to-Market"
      with-company-filter
      with-date-filter
    >
      A report showing the effect of marking the position to the current value vs the prior day’s
      value.
    </report>
    <report
      data-test-id="monthly_billing"
      date-filter-incl-non-workdays
      date-filter-incl-today-from="10:00"
      :sources="[
        { url: '/api/1/broker-reports/billing/export', defaultName: 'report_billing.csv' },
      ]"
      title="Monthly Billing"
      with-company-filter
      with-date-filter
    >
      A report showing month to date Billing in CSV.
    </report>
    <report
      data-test-id="dividend_activity"
      date-filter-incl-today-from="18:05"
      :sources="[
        {
          url: '/api/1/broker-reports/dividend-activity/export',
          defaultName: 'dividend_activity_report.csv',
        },
      ]"
      title="Dividend Activity Report"
      with-company-filter
      with-date-filter
    >
      A report showing dividend activity.
    </report>
    <report
      data-test-id="betanxt_positions"
      date-filter-incl-today-from="16:00"
      :sources="[
        {
          url: '/api/1/broker-reports/betanxt/positions/export',
          defaultName: 'LOANPCPY.txt',
        },
      ]"
      title="BetaNXT Positions File"
      with-company-filter
      with-date-filter
    >
      A report with position changes for BetaNXT.
    </report>
    <report
      data-test-id="betanxt_activity"
      date-filter-incl-today-from="16:00"
      :sources="[
        {
          url: '/api/1/broker-reports/betanxt/activity/export',
          defaultName: 'LOANTCPY.txt',
        },
      ]"
      title="BetaNXT Activity File"
      with-company-filter
      with-date-filter
    >
      A report with activity for BetaNXT.
    </report>
    <report
      data-test-id="executions"
      date-filter-incl-today-from="00:00"
      :sources="[
        {
          url: '/api/1/broker-reports/executions/export',
          defaultName: 'report_executions.csv',
        },
      ]"
      title="Executions"
      with-company-filter
      with-date-range
    >
      A report showing daily executions. Capable of showing executions for a specific date range.
    </report>
  </reports>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import Reports from '@/modules/reports/components/Reports.vue';
import Report from '@/modules/reports/components/Report.vue';

@Component({
  components: {
    Reports,
    Report,
  },
})
export default class BrokerReportsView extends Vue {}
</script>
